import { useEffect } from "react";

const SITE_NAME = "signalSolution";
const BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://signalslt.de";
const DEFAULT_TITLE = `${SITE_NAME} — Websites die Kunden gewinnen`;
const DEFAULT_DESCRIPTION =
  "signalSolution — Professionelle Websites für lokale Unternehmen. KI-gestützter Website-Builder, Online-Buchungssystem und SEO — live in 48 Stunden.";

function setMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectSchema(schema) {
  // Remove old injected schema scripts
  document.querySelectorAll('script[data-seo="true"]').forEach((s) => s.remove());
  const items = Array.isArray(schema) ? schema : [schema];
  items.forEach((s) => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", "true");
    el.textContent = JSON.stringify(s);
    document.head.appendChild(el);
  });
}

export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  noIndex = false,
  schema = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : null;

  useEffect(() => {
    // HTML lang
    document.documentElement.lang = "de";

    // Title
    document.title = fullTitle;

    // Basic meta
    setMeta("description", description);
    setMeta("robots", noIndex ? "noindex,nofollow" : "index,follow");

    // Open Graph
    setMeta("og:title", fullTitle, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "website", true);
    setMeta("og:locale", "de_DE", true);
    setMeta("og:site_name", SITE_NAME, true);
    if (canonicalUrl) setMeta("og:url", canonicalUrl, true);

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);

    // Canonical
    if (canonicalUrl) setLink("canonical", canonicalUrl);

    // Schema.org JSON-LD
    if (schema) injectSchema(schema);

    // Cleanup title on unmount
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [fullTitle, description, canonicalUrl, noIndex, schema]);

  return null;
}

// ─── Fertige Schema-Objekte ───────────────────────────────────────────────────

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "signalSolution",
  description:
    "Professionelle Websites für lokale Unternehmen — KI-gestützte Erstellung, SEO, Online-Buchung, live in 48 Stunden.",
  url: BASE_URL,
  email: "info@signalslt.de",
  serviceType: "Website-Erstellung und digitales Marketing",
  areaServed: { "@type": "Country", name: "Deutschland" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Website-Pakete",
    itemListElement: [
      { "@type": "Offer", name: "Starter", description: "1-seitige professionelle Website für Selbstständige" },
      { "@type": "Offer", name: "Business", description: "Bis zu 5 Unterseiten + Online-Buchungssystem" },
      { "@type": "Offer", name: "Premium", description: "Individuelle Website mit aktivem SEO und Strategy-Call" },
    ],
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: BASE_URL,
};
